/**
 * The periods a charge plan bills (L23 of docs/plans/ledger.md): a month, a
 * quarter or a financial year, cut from the workspace's financial year start
 * (L42), so a quarter of an April year runs April to June and a yearly plan
 * bills on 1 April. A month starts on the year's start day: the 1st for
 * almost every business.
 *
 * Pure, and shared, so the server's run and a screen that says "Next run:
 * 1 Jul" read one calendar. Days are `YYYY-MM-DD` and the end is inclusive.
 */
import type { ChargeCadence } from '../api';
import { fiscalYearBounds } from '../fiscal-year';
import type { FiscalYearStart } from '../tax-countries';

export const CHARGE_CADENCES: readonly ChargeCadence[] = ['monthly', 'quarterly', 'yearly'];

export const isChargeCadence = (value: unknown): value is ChargeCadence =>
  typeof value === 'string' && (CHARGE_CADENCES as readonly string[]).includes(value);

/** A plan's charge for a party who joins after the period starts: the full charge, or a share by days. */
export type FirstPeriod = 'full' | 'by_days';

export type ChargePeriod = { readonly start: string; readonly end: string };

const MONTHS: Readonly<Record<ChargeCadence, number>> = { monthly: 1, quarterly: 3, yearly: 12 };

const pad = (n: number, width = 2): string => String(n).padStart(width, '0');

/** A day as a count of days since 1970-01-01. */
const dayNumber = (day: string): number => Math.round(Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10))) / 86_400_000);

const dayOfNumber = (n: number): string => new Date(n * 86_400_000).toISOString().slice(0, 10);

/** `day` moved by whole days. */
export const shiftDay = (day: string, days: number): string => dayOfNumber(dayNumber(day) + days);

/** How many days from `from` to `to`, both counted. */
export const daysIn = (from: string, to: string): number => dayNumber(to) - dayNumber(from) + 1;

/** The start day `months` after `year-month` with the year's start day (1 to 28, so every month holds it). */
const monthStart = (year: number, month: number, months: number, day: number): string => {
  const index = year * 12 + (month - 1) + months;
  return `${pad(Math.floor(index / 12), 4)}-${pad((index % 12) + 1)}-${pad(day)}`;
};

/** The period of this cadence that holds `day`. */
export const chargePeriodOf = (day: string, cadence: ChargeCadence, year: FiscalYearStart): ChargePeriod => {
  const { from } = fiscalYearBounds(day, year);
  const step = MONTHS[cadence];
  const fyYear = Number(from.slice(0, 4));
  for (let i = 0; i < 12; i += step) {
    const start = monthStart(fyYear, year.month, i, year.day);
    const next = monthStart(fyYear, year.month, i + step, year.day);
    if (day >= start && day < next) return { start, end: shiftDay(next, -1) };
  }
  // Unreachable: the financial year holds the day.
  throw new Error(`no ${cadence} period holds ${day}`);
};

/** The period that follows `period`. */
export const nextChargePeriod = (period: ChargePeriod, cadence: ChargeCadence, year: FiscalYearStart): ChargePeriod =>
  chargePeriodOf(shiftDay(period.end, 1), cadence, year);

/**
 * The days of the period a party was active, from its periods: every stretch
 * that overlaps it, cut to it. A party with no period at all (a list that
 * keeps no dates) is active every day.
 */
export const activeDaysIn = (
  period: ChargePeriod,
  stretches: readonly { readonly activeFrom: string; readonly activeTo: string | null }[] | null,
): { readonly days: number; readonly firstDay: string | null } => {
  if (stretches === null || stretches.length === 0) return { days: daysIn(period.start, period.end), firstDay: period.start };
  let days = 0;
  let firstDay: string | null = null;
  for (const one of stretches) {
    const from = one.activeFrom > period.start ? one.activeFrom : period.start;
    const to = one.activeTo === null || one.activeTo > period.end ? period.end : one.activeTo;
    if (from > to) continue;
    days += daysIn(from, to);
    if (firstDay === null || from < firstDay) firstDay = from;
  }
  return { days, firstDay };
};

/** `minor x part / whole`, rounded half away from zero: a charge by days. */
export const shareByDays = (minor: bigint, part: number, whole: number): bigint => {
  if (part >= whole) return minor;
  const n = minor * BigInt(part);
  const d = BigInt(whole);
  const q = n / d;
  const r = n % d;
  return (r < 0n ? -r : r) * 2n >= d ? (n < 0n ? q - 1n : q + 1n) : q;
};

/** What a party is charged for a period, as a share: the whole, or by the days it was active when it joined after the start. */
export const chargeShare = (
  period: ChargePeriod,
  stretches: readonly { readonly activeFrom: string; readonly activeTo: string | null }[] | null,
  first: FirstPeriod,
): { readonly part: number; readonly whole: number; readonly firstDay: string } | null => {
  const active = activeDaysIn(period, stretches);
  if (active.days === 0 || active.firstDay === null) return null;
  const whole = daysIn(period.start, period.end);
  // Only a first part period is charged by days: a party who leaves inside a period pays it whole.
  const joinedLate = active.firstDay > period.start;
  return { part: first === 'by_days' && joinedLate ? active.days : whole, whole, firstDay: active.firstDay };
};

/**
 * Which cycle of a discount a period is (G9, B1 of docs/plans/gym-membership.md):
 * the periods from the one that holds `from` up to this one, plus 1. Counted
 * by period, never by charges written, so a deleted charge or a run tried
 * again gives the same answer. 0 or less when `from` is after the period.
 */
export const chargeCycleOf = (from: string, period: ChargePeriod, cadence: ChargeCadence, year: FiscalYearStart): number => {
  if (from > period.end) return 0;
  const first = chargePeriodOf(from, cadence, year).start;
  const months = (day: string): number => Number(day.slice(0, 4)) * 12 + Number(day.slice(5, 7)) - 1;
  // Both starts fall on the year's start day, so whole months part them.
  return Math.round((months(period.start) - months(first)) / MONTHS[cadence]) + 1;
};
